import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/SEOHead';

const serviceCategories = [
  {
    id: 'registration',
    icon: 'ri-car-line',
    title: 'Vehicle Registration & Renewal',
    tagline: 'Skip the DMV line — we handle it all.',
    description:
      'Whether you\'re registering a brand-new vehicle or renewing an existing one, our team processes everything quickly and accurately. We handle all Clark County and Nevada DMV requirements so you don\'t have to wait in long government lines.',
    details: [
      'New vehicle registration for cars, trucks, motorcycles, and trailers',
      'Annual registration renewals — in-person or assisted',
      'Out-of-state vehicle registration transfers to Nevada',
      'Commercial vehicle registration',
      'Delinquent registration resolution',
      'Duplicate registration cards and stickers',
    ],
    image: 'https://public.readdy.ai/ai/img_res/f36b55561c016a5076cd0b2c2d3141fb.jpg',
  },
  {
    id: 'title',
    icon: 'ri-file-text-line',
    title: 'Title Transfers & Tag Services',
    tagline: 'Ownership changes made simple.',
    description:
      'Buying or selling a vehicle? Our title transfer specialists ensure every document is completed correctly the first time. We also handle license plate tag issuance, replacements, and specialty plates.',
    details: [
      'Private-party title transfers (buyer & seller)',
      'Dealer title transfers and fleet processing',
      'Lien releases and lien additions',
      'Salvage and rebuilt title processing',
      'License plate tag issuance and renewal',
      'Specialty and personalized plate requests',
    ],
    image: 'https://readdy.ai/api/search-image?query=Close-up%20of%20vehicle%20title%20documents%20and%20license%20plates%20on%20a%20clean%20office%20desk%2C%20professional%20document%20processing%20environment%2C%20organized%20paperwork%20with%20pens%20and%20stamps%2C%20neutral%20background%20with%20soft%20lighting&width=700&height=450&seq=svc-title-001&orientation=landscape',
  },
  {
    id: 'vin',
    icon: 'ri-search-eye-line',
    title: 'VIN Inspections',
    tagline: 'Fast, certified, and on-site.',
    description:
      'Our certified VIN inspectors verify vehicle identification numbers for out-of-state transfers, rebuilt titles, and other DMV requirements. We provide official documentation accepted by the Nevada DMV.',
    details: [
      'Nevada DMV-compliant VIN verification',
      'Out-of-state vehicle VIN inspections',
      'Rebuilt and salvage title VIN checks',
      'Motorcycle and trailer VIN inspections',
      'Same-day inspection reports',
      'Documentation accepted at all Nevada DMV offices',
    ],
    image: 'https://readdy.ai/api/search-image?query=Professional%20vehicle%20inspector%20examining%20a%20car%20VIN%20number%20with%20a%20clipboard%20and%20flashlight%20in%20a%20clean%20inspection%20area%2C%20official%20inspection%20process%2C%20neutral%20garage%20background%20with%20good%20lighting%2C%20professional%20uniform&width=700&height=450&seq=svc-vin-001&orientation=landscape',
  },
  {
    id: 'smog',
    icon: 'ri-shield-check-line',
    title: 'Smog Check Guidance',
    tagline: 'Navigate emissions requirements with confidence.',
    description:
      'Confused about Nevada smog check requirements? We guide you through the entire process — from understanding exemptions to finding certified stations and resolving failed tests.',
    details: [
      'Smog check requirement assessment',
      'Referrals to certified smog stations',
      'Failed smog test resolution guidance',
      'Smog exemption verification',
      'Out-of-state smog compliance assistance',
      'Smog waiver application support',
    ],
    image: 'https://readdy.ai/api/search-image?query=Vehicle%20emissions%20testing%20station%20with%20professional%20technician%20connecting%20diagnostic%20equipment%20to%20a%20car%2C%20clean%20modern%20auto%20service%20environment%2C%20organized%20tools%20and%20equipment%2C%20professional%20setting%20with%20good%20lighting&width=700&height=450&seq=svc-smog-001&orientation=landscape',
  },
  {
    id: 'documents',
    icon: 'ri-file-edit-line',
    title: 'Document Preparation',
    tagline: 'Every form, filled right the first time.',
    description:
      'DMV paperwork can be confusing and costly if done incorrectly. Our document specialists prepare, review, and organize all required forms to ensure accuracy and avoid rejections or delays.',
    details: [
      'DMV form completion and review',
      'Bill of sale preparation',
      'Power of attorney for vehicle transactions',
      'Odometer disclosure statements',
      'Affidavit of heirship for inherited vehicles',
      'Notary referrals for required documents',
    ],
    image: 'https://readdy.ai/api/search-image?query=Professional%20office%20worker%20carefully%20reviewing%20and%20filling%20out%20official%20documents%20at%20a%20clean%20organized%20desk%2C%20paperwork%20and%20forms%20neatly%20arranged%2C%20pen%20in%20hand%2C%20professional%20business%20environment%20with%20warm%20lighting&width=700&height=450&seq=svc-docs-001&orientation=landscape',
  },
  {
    id: 'support',
    icon: 'ri-customer-service-line',
    title: 'Concierge Client Support',
    tagline: 'A real person, every step of the way.',
    description:
      'We believe every client deserves personalized attention. Our concierge support team walks you through your specific situation, answers questions, and ensures your experience is smooth from start to finish.',
    details: [
      'One-on-one consultation for complex cases',
      'Status updates throughout your transaction',
      'Follow-up support after service completion',
      'Multilingual assistance available',
      'Assistance for elderly and disabled clients',
      'Corporate and fleet account management',
    ],
    image: 'https://readdy.ai/api/search-image?query=Friendly%20professional%20customer%20service%20representative%20smiling%20and%20assisting%20a%20client%20at%20a%20modern%20reception%20desk%2C%20welcoming%20office%20environment%2C%20clean%20and%20organized%20workspace%2C%20warm%20professional%20lighting%2C%20Las%20Vegas%20business%20setting&width=700&height=450&seq=svc-support-001&orientation=landscape',
  },
];

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(serviceCategories[0].id);
  const active = serviceCategories.find((s) => s.id === activeService)!;
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/services#webpage`,
        url: `${siteUrl}/services`,
        name: 'DMV Services Las Vegas | Vehicle Registration, Title Transfers & VIN Inspections',
        description: 'Villarreal Enterprise Group offers comprehensive DMV services in Las Vegas: vehicle registration, title transfers, VIN inspections, smog guidance, and document preparation. Fast, accurate, and client-focused.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: { '@id': `${siteUrl}/services#breadcrumb` },
        dateModified: '2026-04-11',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/services#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        ],
      },
      {
        '@type': 'Service',
        provider: { '@id': `${siteUrl}/#organization` },
        areaServed: { '@type': 'State', name: 'Nevada' },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'DMV & Document Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Vehicle Registration & Renewal',
                description: 'New vehicle registration, annual renewals, out-of-state transfers, and commercial vehicle registration in Las Vegas.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Title Transfers & Tag Services',
                description: 'Private-party and dealer title transfers, lien releases, salvage title processing, and license plate tag services.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'VIN Inspections',
                description: 'Nevada DMV-compliant VIN verification for out-of-state vehicles, rebuilt titles, motorcycles, and trailers.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Smog Check Guidance',
                description: 'Smog check requirement assessment, referrals to certified stations, and failed test resolution guidance.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Document Preparation',
                description: 'DMV form completion, bill of sale preparation, power of attorney, and odometer disclosure statements.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Concierge Client Support',
                description: 'One-on-one consultation, multilingual assistance, and personalized support for complex DMV cases.',
              },
            },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Do I need an appointment for DMV services in Las Vegas?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Speedy DMV Services™ offers walk-in and same-day service for most standard transactions. For complex cases handled by DMV-Xpress Services™, we recommend contacting us in advance.',
            },
          },
          {
            '@type': 'Question',
            name: 'How long does a VIN inspection take in Las Vegas?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our certified VIN inspections are typically completed same-day. We provide official documentation accepted at all Nevada DMV offices.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can you help with out-of-state vehicle registration in Nevada?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. We handle out-of-state vehicle registration transfers to Nevada, including all required documentation, VIN inspections, and DMV form preparation.',
            },
          },
          {
            '@type': 'Question',
            name: 'What documents do I need for a title transfer in Nevada?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Typically you need the signed title, a bill of sale, odometer disclosure, and valid ID. Our document specialists will review your specific situation and prepare all required paperwork correctly.',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="DMV Services Las Vegas | Vehicle Registration, Title Transfers & VIN Inspections"
        description="Villarreal Enterprise Group offers comprehensive DMV services in Las Vegas: vehicle registration, title transfers, VIN inspections, smog guidance, and document preparation. Fast, accurate, and client-focused."
        keywords="Las Vegas DMV services, vehicle registration Nevada, title transfer Las Vegas, VIN inspection, document preparation DMV"
        canonical={`${siteUrl}/services`}
        schema={schema}
        schemaId="schema-services"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gray-900 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20building%20exterior%20at%20dusk%20with%20warm%20interior%20lights%20glowing%20through%20large%20windows%2C%20clean%20architectural%20lines%2C%20Las%20Vegas%20Nevada%20business%20district%2C%20minimalist%20corporate%20aesthetic%20with%20dark%20sky%20background&width=1400&height=500&seq=svc-hero-001&orientation=landscape"
            alt="Services hero"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-gray-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Services</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Our Services
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
            Comprehensive DMV and document services delivered through our two trusted brands — 
            Speedy DMV Services™ and DMV-Xpress Services™.
          </p>
        </div>
      </section>

      {/* Service Selector */}
      <section className="bg-white border-b border-gray-100 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-1 py-3 scrollbar-hide">
            {serviceCategories.map((svc) => (
              <button
                key={svc.id}
                onClick={() => setActiveService(svc.id)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeService === svc.id
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className={`${svc.icon} text-base`}></i>
                {svc.title.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Service Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 mb-6">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${active.icon} text-gray-700 text-base`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{active.tagline}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{active.title}</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">{active.description}</p>
              <ul className="space-y-3 mb-10">
                {active.details.map((detail, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-gray-900 text-lg"></i>
                    </div>
                    <span className="text-gray-700">{detail}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line"></i>
                Inquire About This Service
              </Link>
            </div>
            <div className="relative">
              <img
                src={active.image}
                alt={active.title}
                className="w-full h-[420px] object-cover object-top rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything We Offer</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click any service above to explore details, or browse the full overview below.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCategories.map((svc) => (
              <button
                key={svc.id}
                onClick={() => {
                  setActiveService(svc.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`text-left bg-white rounded-xl p-6 border transition-all cursor-pointer hover:border-gray-400 ${
                  activeService === svc.id ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg mb-4">
                  <i className={`${svc.icon} text-2xl text-gray-800`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{svc.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{svc.description}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Serving Greater Las Vegas</h2>
          <p className="text-gray-400 mb-12 max-w-xl mx-auto">
            Our services are available throughout Clark County, Nevada.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-3xl mx-auto">
            {['Las Vegas', 'Henderson', 'North Las Vegas', 'Paradise', 'Sunrise Manor'].map((city) => (
              <div key={city} className="bg-white/10 rounded-lg p-4 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-map-pin-line text-xl text-gray-300"></i>
                </div>
                <div className="text-sm font-medium">{city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
          <p className="text-gray-600 mb-8">
            Contact our team today and we'll match you with the right service for your needs.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contact Us
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
